import { useState } from 'react';
import { services } from '../../../mocks/services';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    carMake: '',
    carYear: '',
    services: [] as string[],
    preferredDate: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleServiceToggle = (serviceName: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceName)
        ? prev.services.filter(s => s !== serviceName)
        : [...prev.services, serviceName]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.carMake) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.notes.length > 500) {
      alert('Additional notes must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('carMake', formData.carMake);
      formBody.append('carYear', formData.carYear);
      formBody.append('services', formData.services.join(', '));
      formBody.append('preferredDate', formData.preferredDate);
      formBody.append('notes', formData.notes);

      const response = await fetch('https://readdy.ai/api/form/d674994fomltbg1qank0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          carMake: '',
          carYear: '',
          services: [],
          preferredDate: '',
          notes: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-gradient-to-b from-primary-black to-primary-dark relative overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(0, 212, 255, 0.03) 50px, rgba(0, 212, 255, 0.03) 51px),
                         repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(0, 212, 255, 0.03) 50px, rgba(0, 212, 255, 0.03) 51px)`
      }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-4xl md:text-5xl font-orbitron font-bold text-white mb-6" style={{ textShadow: '0 0 30px rgba(0, 212, 255, 0.6)' }}>
              BOOK YOUR APPOINTMENT
            </h2>
            <p className="text-gray-400 font-inter text-lg mb-10">
              Fill out your details and we'll contact you via WhatsApp
            </p>

            <form id="car_detailing_contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                  placeholder="Your Name *"
                />
              </div>

              <div className="relative">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                  placeholder="Phone Number *"
                />
              </div>

              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                  placeholder="Email Address *"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="carMake"
                  value={formData.carMake}
                  onChange={(e) => setFormData({ ...formData, carMake: e.target.value })}
                  required
                  className="px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                  placeholder="Car Make/Model *"
                />
                <input
                  type="number"
                  name="carYear"
                  value={formData.carYear}
                  onChange={(e) => setFormData({ ...formData, carYear: e.target.value })}
                  className="px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                  placeholder="Year"
                />
              </div>

              <div className="relative">
                <label className="block text-primary-blue font-inter text-sm font-semibold mb-3">
                  Select Services (Multiple)
                </label>
                <div className="max-h-48 overflow-y-auto bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl p-4 space-y-2" style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}>
                  {services.map((service) => (
                    <label key={service.id} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service.name)}
                        onChange={() => handleServiceToggle(service.name)}
                        className="w-5 h-5 accent-primary-blue cursor-pointer"
                      />
                      <span className="text-white font-inter text-sm group-hover:text-primary-blue transition-colors">
                        {service.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="relative">
                <input
                  type="date"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                  className="w-full px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                />
              </div>

              <div className="relative">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  maxLength={500}
                  rows={4}
                  className="w-full px-6 py-4 bg-primary-dark/50 backdrop-blur-sm border-2 border-primary-blue/30 rounded-xl text-white font-inter focus:outline-none focus:border-primary-blue transition-all duration-300 resize-none"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.1)' }}
                  placeholder="Additional Notes (Max 500 characters)"
                ></textarea>
                <p className="text-gray-500 text-xs mt-1 text-right">{formData.notes.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 bg-primary-blue text-white font-inter font-bold text-lg rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}
              >
                {isSubmitting ? 'SUBMITTING...' : 'SCHEDULE NOW'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500 rounded-xl text-green-400 font-inter text-center">
                  Thank you! We'll contact you via WhatsApp soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500 rounded-xl text-red-400 font-inter text-center">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-primary-dark/50 backdrop-blur-sm rounded-3xl p-8 border-2 border-primary-blue/30" style={{ boxShadow: '0 0 20px rgba(0, 212, 255, 0.2)' }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-blue/20 border border-primary-blue">
                  <i className="ri-map-pin-line text-2xl text-primary-blue"></i>
                </div>
                <h3 className="text-2xl font-orbitron font-bold text-white">OUR LOCATION</h3>
              </div>
              <p className="text-white font-inter text-lg mb-2">Elkhart, Indiana</p>
              <p className="text-gray-400 font-inter text-sm mb-6">Serving Local Area Only</p>
              <div className="rounded-2xl overflow-hidden border-2 border-primary-blue/30" style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95234.89384190384!2d-85.9767!3d41.6819!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8816c4f0e6c3e3e3%3A0x1234567890abcdef!2sElkhart%2C%20IN!5e0!3m2!1sen!2sus!4v1234567890123"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Marche Location"
                ></iframe>
              </div>
            </div>

            <div className="bg-primary-dark/50 backdrop-blur-sm rounded-3xl p-8 border-2 border-primary-blue/30" style={{ boxShadow: '0 0 20px rgba(0, 212, 255, 0.2)' }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-blue/20 border border-primary-blue">
                  <i className="ri-phone-line text-2xl text-primary-blue"></i>
                </div>
                <h3 className="text-2xl font-orbitron font-bold text-white">CONTACT</h3>
              </div>
              <a
                href="https://wa.me/12694649163"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white hover:text-primary-blue transition-all duration-300 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-3xl"></i>
                <div>
                  <p className="font-inter text-xl font-bold">(269) 464-9163</p>
                  <span className="text-sm text-green-400">WhatsApp Only</span>
                </div>
              </a>
            </div>

            <div className="bg-primary-dark/50 backdrop-blur-sm rounded-3xl p-8 border-2 border-primary-blue/30" style={{ boxShadow: '0 0 20px rgba(0, 212, 255, 0.2)' }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-blue/20 border border-primary-blue">
                  <i className="ri-time-line text-2xl text-primary-blue"></i>
                </div>
                <h3 className="text-2xl font-orbitron font-bold text-white">HOURS</h3>
              </div>
              <div className="space-y-3 text-white font-inter">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="text-primary-blue">8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="text-primary-blue">9:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="text-gray-500">Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;